<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <TopNavbar />

    <!-- Header -->
    <header class="flex justify-between items-center bg-dark-grey p-4 mb-4">
      <h1 class="text-xl font-semibold">Edit Activation</h1>
      <div class="flex items-center text-sm gap-2">
        <img
          v-if="isLoading"
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-8 h-8"
        />

        <!-- Save Button -->
        <button
          class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          :class="{ 'opacity-50': isLoading == true }"
          :disabled="isLoading == true"
          @click="submitForm"
        >
          Save
        </button>
        <!-- Cancel Button -->
        <button
          class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          :disabled="isLoading"
          @click="cancelForm"
        >
          Cancel
        </button>
      </div>
    </header>

    <!-- Activation Form -->
    <form
      class="flex flex-1 overflow-y-auto flex-col pb-6 space-y-6 px-4"
      :class="{ 'opacity-50': isLoading }"
      @submit.prevent="submitForm"
    >
      <!-- Project Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="project" class="text-sm text-left font-medium">Project *</label>
        <CustomDropdown
          id="project"
          v-model="form.project"
          disabled="true"
          :options="projectOptions"
          placeholder="Select Project"
        />
        <span v-if="errors.project" class="text-red-500 text-xs text-left pl-1">{{
          errors.project
        }}</span>
      </div>

      <!-- Agent Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="agent" class="text-sm font-medium text-left">Agent *</label>
        <CustomDropdown
          id="agent"
          v-model="form.agent"
          :options="agentOptions"
          placeholder="Select Agent"
        />
        <span v-if="errors.agent" class="text-red-500 text-xs text-left pl-1">{{
          errors.agent
        }}</span>
      </div>

      <!-- Instructions Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="instructions" class="text-sm font-medium text-left"
          >Instructions *</label
        >
        <CustomDropdown
          id="instructions"
          v-model="form.instructions"
          :options="instructionsOptions"
          placeholder="Select Instructions"
        />
        <span v-if="errors.instructions" class="text-red-500 text-xs text-left pl-1">{{
          errors.instructions
        }}</span>
      </div>

      <!-- Agent Email Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="agent-email" class="text-sm font-medium text-left"
          >Agent Email Address *</label
        >
        <CustomDropdown
          id="agent-email"
          v-model="form.agentEmail"
          disabled="true"
          :options="agentEmailOptions"
          placeholder="Select Agent Email"
        />
        <span v-if="errors.agentEmail" class="text-red-500 text-xs text-left pl-1">{{
          errors.agentEmail
        }}</span>
      </div>

      <!-- Agent Phone Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="agent-phone" class="text-sm font-medium text-left"
          >Agent Phone Number *</label
        >
        <CustomDropdown
          id="agent-phone"
          disabled="true"
          v-model="form.agentPhone"
          :options="agentPhoneOptions"
          placeholder="Select Agent Phone"
        />
        <span v-if="errors.agentPhone" class="text-red-500 text-xs text-left pl-1">{{
          errors.agentPhone
        }}</span>
      </div>

      <!-- Project Inbox Phone Dropdown -->
      <div class="flex flex-col gap-1">
        <label for="inbox-phone" class="text-sm font-medium text-left"
          >Project Inbox Phone Number *</label
        >
        <CustomDropdown
          id="inbox-phone"
          v-model="form.projectInboxPhone"
          disabled="true"
          :options="projectInboxPhoneOptions"
          placeholder="Select Project Inbox Phone Number"
        />
        <span
          v-if="errors.projectInboxPhone"
          class="text-red-500 text-xs text-left pl-1"
          >{{ errors.projectInboxPhone }}</span
        >
      </div>
    </form>
    <ErrorModal
      :isOpen="showErrorModal"
      heading="An error occurred"
      :message="errorModalText"
      linkText="Contact Us"
      contactRoute="/contact"
      @close="showErrorModal = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import CustomDropdown from "@/components/CustomDropdown.vue";
import { useActivationStore } from "@/stores/activation";
import { useAgentStore } from "@/stores/agent";
import { useProjectStore } from "@/stores/project";
import { useInstructionStore } from "@/stores/instruction";
import ErrorModal from "@/components/ErrorModal.vue";

const router = useRouter();
const route = useRoute();

const form = ref({
  project: "",
  agent: "",
  instructions: "",
  agentEmail: "",
  agentPhone: "",
  projectInboxPhone: "",
});

const errors = ref({});
const projectOptions = ref([]);
const agentOptions = ref([]);
const instructionsOptions = ref([]);
const agentEmailOptions = ref([]);
const agentPhoneOptions = ref([]);
const projectInboxPhoneOptions = ref([]);
const isLoading = ref(true);
const showErrorModal = ref(false);
const errorModalText = ref("");

const { fetchAgents } = useAgentStore();
const { fetchInstructions } = useInstructionStore();
const { fetchAllProjects } = useProjectStore();
const activationStore = useActivationStore();

// Validation logic
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!form.value.project) errors.value.project = "Project is required.";
  if (!form.value.agent) errors.value.agent = "Agent is required.";
  if (!form.value.instructions) errors.value.instructions = "Instructions are required.";
  if (!form.value.agentPhone) errors.value.agentPhone = "Agent Phone is required.";
  if (!form.value.projectInboxPhone)
    errors.value.projectInboxPhone = "Inbox Phone is required.";

  return isValid;
};

// Submit form
const submitForm = async () => {
  isLoading.value = true;
  if (validateForm()) {
    try {
      const activationData = {
        project_id: form.value.project,
        agent_id: form.value.agent,
        is_active: true,
        instruction_id: form.value.instructions,
        agent_email_id: form.value.agentEmail,
        agent_phone: form.value.agentPhone.toString(),
      };

      // TODO
      //await useActivationStore().deleteProject(form.value.project);

      await activationStore.activateProject(activationData);

      await useActivationStore().fetchActivations();

      router.push({ name: "ActivationsPage" });
    } catch (error) {
      console.error("Error saving activation:", error);
    }
  }
};

// Cancel form
const cancelForm = () => {
  console.log(route.params.id);
  router.push({
    name: "ViewActivationPage",
    params: { id: route.params.id },
  });
};
function handleFetchError(err) {
  let raw = "";
  const data = err.response?.data;

  if (typeof data?.message === "string") {
    raw = data.message;
  } else if (Array.isArray(data?.detail)) {
    // join all validation msgs into one string
    raw = data.detail.map((d) => d.msg || "").join(", ");
  } else if (typeof data?.detail === "string") {
    raw = data.detail;
  }

  const msg = raw || err.message || "";

  if (
    msg.includes("Default Agent has not been set") ||
    msg.includes("Default Instructions have not been set")
  ) {
    errorModalText.value = msg;
    showErrorModal.value = true;
  } else {
    console.error(err);
    throw err;
  }
}

onMounted(async () => {
  try {
    // Fetch necessary dropdown data
    await fetchAllProjects();
    try {
      await fetchAgents();
    } catch (err) {
      return handleFetchError(err);
    }
    try {
      await fetchInstructions();
    } catch (err) {
      return handleFetchError(err);
    }
    await activationStore.getUnassignedEmails();
    await activationStore.getUnassignedPhoneNumbers();

    // Populate dropdown options
    projectOptions.value = useProjectStore().projects.map(({ id, address_1 }) => ({
      label: address_1,
      value: id,
    }));
    agentOptions.value = useAgentStore().agents.map(({ id, agent_name }) => ({
      label: agent_name,
      value: id,
    }));
    instructionsOptions.value = useInstructionStore().instructions.map(
      ({ id, name }) => ({
        label: name,
        value: id,
      })
    );
    agentEmailOptions.value = activationStore.unassignedEmails.map(({ email, id }) => ({
      label: email,
      value: id,
    }));
    agentPhoneOptions.value = activationStore.unassignedPhoneNumbers.map(
      ({ phone_number }) => ({
        label: phone_number,
        value: phone_number,
      })
    );
    projectInboxPhoneOptions.value = activationStore.unassignedPhoneNumbers.map(
      ({ phone_number }) => ({
        label: phone_number,
        value: phone_number,
      })
    );

    // Fetch activation details based on route param `id`
    const { id } = router.currentRoute.value.params;
    if (id) {
      const activationData = await activationStore.getActivation(id);

      if (activationData) {
        form.value = {
          project: activationData.project?.id || "",
          agent: activationData.activation?.agent?.id || "",
          instructions: activationData.activation?.instruction?.id || "",
          agentEmail: activationData.activation?.agent_email_id || "",
          agentPhone: activationData.activation?.agent_stakeholder?.phone || "",
          projectInboxPhone:
            activationData.activation?.agent_stakeholder?.projects?.[0]
              ?.project_manager_twilio_number?.phone_number || "",
        };

        // Dynamically add agentPhone and agentEmail to dropdowns if missing
        if (
          form.value.agentPhone &&
          !agentPhoneOptions.value.some(
            (option) => option.value === form.value.agentPhone
          )
        ) {
          agentPhoneOptions.value.push({
            label: activationData.activation.agent_stakeholder.phone,
            value: activationData.activation.agent_stakeholder.phone,
          });
        }

        if (
          form.value.agentEmail &&
          !agentEmailOptions.value.some(
            (option) => option.value === form.value.agentEmail
          )
        ) {
          agentEmailOptions.value.push({
            label: activationData.activation.agent_email.email,
            value: activationData.activation.agent_email_id,
          });
        }

        if (
          form.value.projectInboxPhone &&
          !projectInboxPhoneOptions.value.some(
            (option) => option.value === form.value.projectInboxPhone
          )
        ) {
          projectInboxPhoneOptions.value.push({
            label: form.value.projectInboxPhone,
            value: form.value.projectInboxPhone,
          });
        }

        isLoading.value = false;
      }
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
});
</script>
